import React, { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, XCircle, Clock, ShoppingBag, ShoppingCart, ClockCounterClockwise, Trash, HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";

const actionLabel = {
  "submit": { label: "Diajukan", color: "bg-sky-100 text-sky-700" },
  "approve": { label: "Disetujui", color: "bg-emerald-100 text-emerald-700" },
  "reject": { label: "Ditolak", color: "bg-rose-100 text-rose-700" },
  "cancel_approval": { label: "Approval Dibatalkan", color: "bg-amber-100 text-amber-700" },
};

export default function Approvals() {
  const [tab, setTab] = useState("sales-order");
  const [so, setSo] = useState([]);
  const [po, setPo] = useState([]);
  const [fr, setFr] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [canApproveFr, setCanApproveFr] = useState(false);
  const [selected, setSelected] = useState({ "sales-order": new Set(), "purchase-order": new Set(), "fund-request": new Set() });
  const [history, setHistory] = useState([]);

  const load = () => {
    api.get("/approvals/pending-all").then((r) => {
      setSo(r.data.sales_orders || []);
      setPo(r.data.purchase_orders || []);
      setFr(r.data.fund_requests || []);
      setCanApprove(r.data.can_approve);
      setCanApproveFr(!!r.data.can_approve_cashbank);
      setSelected({ "sales-order": new Set(), "purchase-order": new Set(), "fund-request": new Set() });
    });
    api.get("/approvals/history", { params: { limit: 300 } }).then((r) => setHistory(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggle = (kind, id) => {
    const next = new Set(selected[kind]);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected({ ...selected, [kind]: next });
  };

  const deleteHistoryEntry = async (h) => {
    if (!window.confirm(`Hapus riwayat approval ${h.reference || h.id}?`)) return;
    try { await api.delete(`/approvals/history/${h.id}`); toast.success("Riwayat dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };
  const deleteAllHistory = async () => {
    if (!window.confirm(`Hapus SEMUA ${history.length} riwayat approval? Tindakan ini tidak bisa dibatalkan.`)) return;
    try { const r = await api.delete("/approvals/history"); toast.success(`${r.data.deleted} riwayat dihapus`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };
  const toggleAll = (kind, rows) => {
    const next = new Set(selected[kind]);
    if (next.size === rows.length) next.clear();
    else rows.forEach((r) => next.add(r.id));
    setSelected({ ...selected, [kind]: next });
  };

  const approveOne = async (kind, id) => {
    const notes = window.prompt("Catatan approval (opsional):", "") ?? "";
    try { await api.post(`/approvals/${kind}/${id}/approve`, { notes }); toast.success("Disetujui"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const rejectOne = async (kind, id) => {
    const notes = window.prompt("Alasan penolakan (wajib):", "");
    if (!notes) return toast.error("Alasan wajib diisi");
    try { await api.post(`/approvals/${kind}/${id}/reject`, { notes }); toast.success("Ditolak"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const batchApprove = async (kind) => {
    const ids = Array.from(selected[kind]);
    if (ids.length === 0) return toast.error("Pilih minimal 1 dokumen");
    if (!window.confirm(`Setujui ${ids.length} dokumen sekaligus?`)) return;
    const notes = window.prompt("Catatan approval batch (opsional):", "") ?? "";
    try {
      const r = await api.post(`/approvals/${kind}/batch-approve`, { ids, notes });
      toast.success(`${r.data.approved} dokumen disetujui`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const approveFr = async (id) => {
    if (!window.confirm("Setujui pengajuan dana ini? Semua item akan langsung dieksekusi (pembayaran/jurnal terbentuk).")) return;
    try { await api.post(`/fund-requests/${id}/approve`); toast.success("Pengajuan disetujui & dieksekusi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui"); }
  };
  const rejectFr = async (id) => {
    const reason = window.prompt("Alasan penolakan (wajib):", "");
    if (!reason) return toast.error("Alasan wajib diisi");
    try { await api.post(`/fund-requests/${id}/reject`, { reason }); toast.success("Pengajuan ditolak"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak"); }
  };
  const batchApproveFr = async () => {
    const ids = Array.from(selected["fund-request"]);
    if (ids.length === 0) return toast.error("Pilih minimal 1 pengajuan");
    if (!window.confirm(`Setujui ${ids.length} pengajuan sekaligus? Semua item akan langsung dieksekusi.`)) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.post(`/fund-requests/${id}/approve`); ok++; }
      catch { fail++; }
    }
    if (ok) toast.success(`${ok} pengajuan disetujui & dieksekusi`);
    if (fail) toast.error(`${fail} pengajuan gagal disetujui`);
    load();
  };

  const total = so.length + po.length + fr.length;

  const renderTable = (kind, rows) => {
    const allChecked = rows.length > 0 && selected[kind].size === rows.length;
    return (
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden" data-testid={`approvals-table-${kind}`}>
        <div className="p-3 border-b border-stone-100 flex items-center justify-between bg-stone-50">
          <div className="text-sm text-stone-700">
            <b>{rows.length}</b> dokumen menunggu approval
            {selected[kind].size > 0 && <span className="ml-3 text-emerald-700">({selected[kind].size} dipilih)</span>}
          </div>
          {canApprove && rows.length > 0 && (
            <Button size="sm" onClick={() => batchApprove(kind)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`batch-approve-${kind}`}>
              <CheckCircle size={14} className="mr-1" weight="fill" />Setujui yang Dipilih
            </Button>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">{rows.length > 0 && <Checkbox checked={allChecked} onCheckedChange={() => toggleAll(kind, rows)} data-testid={`sel-all-${kind}`} />}</TableHead>
              <TableHead>No</TableHead>
              <TableHead>Tanggal</TableHead>
              <TableHead>{kind === "sales-order" ? "Pelanggan" : "Vendor"}</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Diajukan Oleh</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-16 text-stone-500">
                <Clock size={32} className="mx-auto mb-2 text-stone-300" />
                <p className="text-sm">Tidak ada dokumen menunggu approval</p>
              </TableCell></TableRow>
            ) : rows.map((d) => (
              <TableRow key={d.id} data-testid={`row-${kind}-${d.id}`}>
                <TableCell><Checkbox checked={selected[kind].has(d.id)} onCheckedChange={() => toggle(kind, d.id)} data-testid={`sel-${kind}-${d.id}`} /></TableCell>
                <TableCell className="font-mono-plex text-xs">{d.number}</TableCell>
                <TableCell>{formatDate(d.date)}</TableCell>
                <TableCell className="font-medium">{d.partner_name}</TableCell>
                <TableCell className="tabular-nums">{formatIDR(d.total)}</TableCell>
                <TableCell className="text-xs text-stone-600">{d.submitted_by || "—"}</TableCell>
                <TableCell className="flex gap-1 justify-end">
                  {canApprove && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => approveOne(kind, d.id)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`approve-${kind}-${d.id}`}>
                        <CheckCircle size={13} className="mr-1" />Setujui
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectOne(kind, d.id)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`reject-${kind}-${d.id}`}>
                        <XCircle size={13} className="mr-1" />Tolak
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderFrTable = () => {
    const kind = "fund-request";
    const allChecked = fr.length > 0 && selected[kind].size === fr.length;
    return (
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden" data-testid="approvals-table-fund-request">
        <div className="p-3 border-b border-stone-100 flex items-center justify-between bg-stone-50">
          <div className="text-sm text-stone-700">
            <b>{fr.length}</b> pengajuan menunggu approval
            {selected[kind].size > 0 && <span className="ml-3 text-emerald-700">({selected[kind].size} dipilih)</span>}
          </div>
          {canApproveFr && fr.length > 0 && (
            <Button size="sm" onClick={batchApproveFr} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="batch-approve-fund-request">
              <CheckCircle size={14} className="mr-1" weight="fill" />Setujui yang Dipilih
            </Button>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">{fr.length > 0 && <Checkbox checked={allChecked} onCheckedChange={() => toggleAll(kind, fr)} data-testid="sel-all-fund-request" />}</TableHead>
              <TableHead>No</TableHead>
              <TableHead>Tanggal</TableHead>
              <TableHead>Sumber Dana</TableHead>
              <TableHead>Pemohon</TableHead>
              <TableHead>Total</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fr.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-16 text-stone-500">
                <Clock size={32} className="mx-auto mb-2 text-stone-300" />
                <p className="text-sm">Tidak ada pengajuan dana menunggu approval</p>
              </TableCell></TableRow>
            ) : fr.map((d) => (
              <TableRow key={d.id} data-testid={`row-fund-request-${d.id}`}>
                <TableCell><Checkbox checked={selected[kind].has(d.id)} onCheckedChange={() => toggle(kind, d.id)} data-testid={`sel-fund-request-${d.id}`} /></TableCell>
                <TableCell className="font-mono-plex text-xs">{d.number}</TableCell>
                <TableCell>{formatDate(d.date)}</TableCell>
                <TableCell className="font-medium">{d.cash_bank_name || "—"}</TableCell>
                <TableCell className="text-xs text-stone-600">{d.created_by || "—"}</TableCell>
                <TableCell className="tabular-nums">{formatIDR(d.total_amount)}</TableCell>
                <TableCell className="flex gap-1 justify-end">
                  {canApproveFr && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => approveFr(d.id)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`approve-fund-request-${d.id}`}>
                        <CheckCircle size={13} className="mr-1" />Setujui
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectFr(d.id)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`reject-fund-request-${d.id}`}>
                        <XCircle size={13} className="mr-1" />Tolak
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="bg-white border border-stone-200 rounded-md overflow-hidden" data-testid="approval-history-table">
      <div className="p-3 border-b border-stone-100 bg-stone-50 text-sm text-stone-700 flex items-center justify-between gap-2">
        <span>Menampilkan <b>{history.length}</b> aktivitas approval terakhir (submit / setujui / tolak / batalkan approval).</span>
        {canApprove && history.length > 0 && (
          <Button variant="outline" size="sm" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={deleteAllHistory} data-testid="del-all-history-btn"><Trash size={14} className="mr-1" />Hapus Semua Riwayat</Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Waktu</TableHead>
            <TableHead>Modul</TableHead>
            <TableHead>Aksi</TableHead>
            <TableHead>No Dokumen</TableHead>
            <TableHead>Oleh</TableHead>
            <TableHead>Catatan</TableHead>
            {canApprove && <TableHead className="text-right">Aksi</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.length === 0 ? (
            <TableRow><TableCell colSpan={canApprove ? 7 : 6} className="text-center py-12 text-stone-500 text-sm">Belum ada riwayat approval.</TableCell></TableRow>
          ) : history.map((h) => {
            const [modul, act] = String(h.action || ".").split(".");
            const meta = actionLabel[act] || { label: act, color: "bg-stone-100 text-stone-700" };
            const modulLabel = modul === "sales-order" ? "Pesanan Penjualan" : modul === "purchase-order" ? "Pesanan Pembelian" : modul === "fund-request" ? "Pengajuan Dana" : modul;
            return (
              <TableRow key={h.id} data-testid={`hist-${h.id}`}>
                <TableCell className="text-xs text-stone-600 whitespace-nowrap">{(h.created_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell className="text-xs">{modulLabel}</TableCell>
                <TableCell><span className={`text-[11px] px-2 py-0.5 rounded ${meta.color}`}>{meta.label}</span></TableCell>
                <TableCell className="font-mono-plex text-xs">{h.reference || "—"}</TableCell>
                <TableCell className="text-xs text-stone-700">{h.actor_email || "—"}</TableCell>
                <TableCell className="text-xs text-stone-600 max-w-md truncate" title={h.notes}>{h.notes || "—"}</TableCell>
                {canApprove && <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="text-rose-600 h-7" onClick={() => deleteHistoryEntry(h)} data-testid={`del-hist-${h.id}`} title="Hapus riwayat"><Trash size={14} /></Button>
                </TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="approvals-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Approval Center</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola semua dokumen yang menunggu persetujuan di satu tempat. Pilih beberapa dokumen untuk approve batch.</p>
        </div>
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-4 py-2">
          <Clock size={20} weight="duotone" className="text-amber-700" />
          <span className="text-sm text-amber-900"><b>{total}</b> menunggu approval</span>
        </div>
      </div>

      {!canApprove && (
        <div className="mt-4 bg-stone-50 border border-stone-200 rounded-md p-3 text-xs text-stone-600">
          Anda hanya bisa melihat daftar. Persetujuan hanya dapat dilakukan oleh <b>owner/admin</b>.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="sales-order" data-testid="tab-so-appr">
            <ShoppingBag size={14} className="mr-1" />Pesanan Penjualan ({so.length})
          </TabsTrigger>
          <TabsTrigger value="purchase-order" data-testid="tab-po-appr">
            <ShoppingCart size={14} className="mr-1" />Pesanan Pembelian ({po.length})
          </TabsTrigger>
          <TabsTrigger value="fund-request" data-testid="tab-fr-appr">
            <HandCoins size={14} className="mr-1" />Pengajuan Dana ({fr.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history-appr">
            <ClockCounterClockwise size={14} className="mr-1" />Riwayat ({history.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="sales-order" className="mt-4">{renderTable("sales-order", so)}</TabsContent>
        <TabsContent value="purchase-order" className="mt-4">{renderTable("purchase-order", po)}</TabsContent>
        <TabsContent value="fund-request" className="mt-4">{renderFrTable()}</TabsContent>
        <TabsContent value="history" className="mt-4">{renderHistory()}</TabsContent>
      </Tabs>
    </div>
  );
}
